import { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';

import PlanContainer from '../../../../components/PlanContainer';
import PlanPrice from '../../../../components/PlanPrice';
import PlanDetailsContainer from '../../../../components/PlanDetailsContainer';
import PlanFeaturesList from '../../../../components/PlanFeaturesList';
import CheckMarkList from '../../../../components/CheckMarkList';
import PlanDescription from '../../../../components/PlanDescription';
import { getShortAreaUnit } from '../../../../../shared/features/areaUnit/helpers/functions/areaUnit';
import TRIAL_AREA_AMOUNTS from '../../helpers/constants/areaAmounts';
import usePriceConfig from '../../../../../shared/features/priceConfig/hooks/usePriceConfig';
import rootStore from '../../../rootStore/RootStore';
import useCountries from '../../../../../shared/features/countries/hooks/useCountries';
import CtaTooltipButton from '../CtaTooltipButton';
import useUser from '../../../../../shared/features/user/hooks/useUser';

const FreeTrialPlan = observer(() => {
  const { t } = useTranslation();
  const { isLoaded: priceConfigLoaded } = usePriceConfig(rootStore);
  const { trialAvailableAlpha3List } = useCountries(rootStore);
  const { areaUnitStore } = rootStore;
  const { areaUnit } = areaUnitStore;
  const { userData, isCombinedDataLoaded: userDataLoaded } = useUser(rootStore);
  const userCountry = userData?.country ?? '';
  const countryEligible = trialAvailableAlpha3List.includes(userCountry);
  const trialAreaAmount = TRIAL_AREA_AMOUNTS[areaUnit];

  if (userDataLoaded && !countryEligible) {
    return null;
  }

  return (
    <PlanContainer showPlaceholder={!priceConfigLoaded}>
      <PlanDescription>
        <Fragment key="title">{t('plans.free-trial.title')}</Fragment>
        <Fragment key="description">
          {t('plans.free-trial.description')}
        </Fragment>
        <PlanPrice
          key="price"
          title={t('plans.free-trial.no-credit-card-required')}
        />
      </PlanDescription>
      <CtaTooltipButton />
      <PlanDetailsContainer>
        <PlanFeaturesList
          title={t('plan-select.details.features-included')}
          items={[
            t('plans.free-trial.details.access-to-features'),
            <Trans
              i18nKey="plans.free-trial.details.coverage"
              values={{
                area: trialAreaAmount,
                areaUnit: getShortAreaUnit(areaUnit),
              }}
            />,
            t('plans.free-trial.details.no-credit-card-required'),
            t('plans.free-trial.details.seamless-transition'),
          ]}
        />
      </PlanDetailsContainer>
      <PlanFeaturesList
        title={t('plan-select.details.limitation')}
        items={[t('plans.free-trial.details.no-planet-imagery-included')]}
        icon={
          <RemoveCircleIcon
            sx={(theme) => ({
              color: theme.palette.secondary.light,
            })}
            fontSize="small"
          />
        }
      />
      <CheckMarkList
        title={t('plan-select.benefits')}
        items={[
          {
            title: t('plans.free-trial.benefits.risk-free-evaluation.title'),
            description: t(
              'plans.free-trial.benefits.risk-free-evaluation.description',
            ),
          },
          {
            title: t('plans.free-trial.benefits.easy-sign-up.title'),
            description: t(
              'plans.free-trial.benefits.easy-sign-up.description',
            ),
          },
          {
            title: t('plans.free-trial.benefits.optimization-insights.title'),
            description: t(
              'plans.free-trial.benefits.optimization-insights.description',
            ),
          },
        ]}
      />
      <CheckMarkList
        title={t('plans.free-trial.available-regions.title')}
        items={[
          {
            title: t('plans.free-trial.available-regions.available-in'),
            description: t('plans.free-trial.available-regions.regions'),
          },
        ]}
      />
    </PlanContainer>
  );
});

export default FreeTrialPlan;
